import React, { useMemo, useState } from 'react';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';

import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { LibraryImage } from 'components/DesignSystems/New/TiptapRichTextEditor';

import { Editor } from '@tiptap/react';
import { StylishNewSearchBar } from './StylishNewSearchBar';

type TipTapImageLibraryModalProps = {
  show: boolean;
  onClose: () => void;
  imageLibrary: LibraryImage[];
  editor: Editor; // The editor instance from the TiptapRichTextEditor component
};

const renderTooltip = (text: string) => (
  <Tooltip id={`tooltip-${text.toLowerCase().replace(/\s/g, '-')}`}>
    {text}
  </Tooltip>
);

const TipTapImageLibraryModal: React.FC<TipTapImageLibraryModalProps> = ({
  show,
  onClose,
  imageLibrary,
  editor,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredImages = useMemo(() => {
    if (!imageLibrary) return [];
    return imageLibrary.filter((image) =>
      image.fileName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [imageLibrary, searchTerm]);

  const onClickImage = (image: LibraryImage) => {
    editor.chain().focus().setImage({ src: image.fileUrl }).run();
    onClose();
  };

  return (
    <Modal show={show} size="xl" centered onHide={onClose}>
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>Select an image to insert</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="tiptap-editor__image-dialog">
          <div className="tiptap-editor__image-dialog-content">
            {imageLibrary?.length === 0 && (
              <div className="p-3">
                <em className="text-muted">
                  No images attached to this SITREP section.
                </em>
              </div>
            )}
            {(imageLibrary ?? []).length > 0 && (
              <>
                <div className="m-2">
                  <StylishNewSearchBar
                    value={searchTerm}
                    onChangeSearchTerm={setSearchTerm}
                    placeholder="Search images..."
                  />
                </div>
                <div className="tiptap-editor__image-library">
                  <div
                    className="tiptap-editor__library-image-container"
                    style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}
                  >
                    {filteredImages.map((image) => (
                      <OverlayTrigger
                        key={image.fileName}
                        placement="top"
                        overlay={renderTooltip('Click to insert')}
                      >
                        <div
                          key={image.fileName}
                          className="position-relative"
                          onClick={() => onClickImage(image)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              onClickImage(image);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          style={{ cursor: 'pointer', width: 'fit-content' }}
                        >
                          <div className="position-absolute top-0 start-0 w-100 d-flex justify-content-between align-items-center bg-dark bg-opacity-75 text-white px-2 py-1">
                            <span
                              className="text-truncate"
                              style={{ maxWidth: '70%' }}
                            >
                              {image.fileName}
                            </span>
                          </div>
                          <img
                            src={image.signedUrl}
                            alt={image.fileName}
                            className="img-fluid"
                            width={400}
                          />
                        </div>
                      </OverlayTrigger>
                    ))}
                  </div>
                </div>
              </>
            )}
            <StylishNewButton
              type="button"
              onClick={onClose}
              className="btn btn-secondary btn-sm mt-2"
            >
              Close
            </StylishNewButton>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default TipTapImageLibraryModal;
